/**
 * Service order detail — OS number, store, dates, status, totals, and the
 * item tree (`additional_service` nested under its parent, `product` flat).
 *
 * ⚠️ Deviation from `docs/handoff/service-orders-design-brief.md` §"Order
 * detail": the brief specifies a payment line (`Pago` / `Pago parcialmente —
 * R$ 100,00 de R$ 275,00` / `Em aberto`). The shipped API
 * (`docs/handoff/2026-07-30-read-endpoints-shipped.md`) carries no payment /
 * recebível data at all — "Orçamentos, entregas and recebíveis remain
 * unbuilt" — so that line cannot be rendered honestly and is omitted rather
 * than guessed or hard-coded to a wrong state.
 *
 * Four outcomes:
 *   - loading                          → a status line
 *   - the order does not exist, or belongs to someone else (404, and the two
 *     are deliberately indistinguishable) → a "not found" EmptyState
 *   - any other failure (network, 5xx) → an honest error, never "not found"
 *   - the order                        → header + item tree
 */
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { EmptyState } from '../components/EmptyState.tsx';
import { StatusChip } from '../components/StatusChip.tsx';
import { ServiceOrderItemRow } from '../components/ServiceOrderItemRow.tsx';
import { IconAlert, IconSearch } from '../components/icons.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { buildItemTree, getServiceOrder, type ServiceOrderDetail } from '../api/serviceOrders.ts';
import { formatDate, formatMoney } from '../i18n/format.ts';
import styles from './OrdemServicoDetail.module.css';
import { MONEY_VISIBLE } from '../money-visibility.ts';
import { runtimeEnv } from '../runtimeConfig.ts';

export function OrdemServicoDetail(): React.JSX.Element {
  const { osId } = useParams();
  const { t } = useTranslation();
  const auth = useAuth();
  const accessToken = auth.session?.access_token;
  const bffBase = runtimeEnv('VITE_BFF_BASE_URL') ?? '/bff/v1';

  const { data, isLoading, isError, error, refetch } = useQuery<ServiceOrderDetail, Error>({
    queryKey: ['service-orders', 'detail', osId],
    queryFn: async () => {
      const result = await getServiceOrder(fetch, bffBase, accessToken ?? '', osId ?? '');
      if (result.error) throw new Error(result.error);
      // getServiceOrder only returns a null `data` alongside a set `error`
      // (see its module doc), so reaching here with a null result would be a
      // contract violation, not a customer-facing state. Fail loudly.
      if (!result.data) throw new Error('server');
      return result.data;
    },
    enabled: Boolean(accessToken && osId),
  });

  if (isLoading || !data) {
    if (isError && error.message === 'not_found') {
      return (
        <EmptyState
          icon={<IconSearch size={32} />}
          title={t('orderDetail.notFoundTitle')}
          description={t('orderDetail.notFoundBody')}
        />
      );
    }

    if (isError) {
      return (
        <div className="ns-alert" role="alert">
          <IconAlert size={18} />
          <div>
            <p className={styles['errorTitle']}>{t('orderDetail.loadErrorTitle')}</p>
            <p className={styles['errorBody']}>{t('orderDetail.loadErrorBody')}</p>
            <button type="button" className={`ns-cta ${styles['errorRetry']}`} onClick={() => void refetch()}>
              {t('orderDetail.retry')}
            </button>
          </div>
        </div>
      );
    }

    return <p className={styles['status']}>{t('orderDetail.loading')}</p>;
  }

  const { order, items } = data;
  const tree = buildItemTree(items);

  return (
    <div className={styles['page']}>
      <header className={styles['header']}>
        <div className={styles['headerTop']}>
          <span className={styles['number']}>{t('orderDetail.number', { numero: order.order_id })}</span>
          <StatusChip status={order.status} />
        </div>

        <p className={styles['franchise']}>{order.franchise.name}</p>

        <div className={styles['dates']}>
          <span>{t('orderDetail.openedOn', { date: formatDate(order.created_at) })}</span>
          {order.expected_delivery_at && (
            <span>{t('orderDetail.expectedDelivery', { date: formatDate(order.expected_delivery_at) })}</span>
          )}
        </div>

        {/* see money-visibility.ts — the whole totals block, not just the
            numbers: a label with no value reads as a rendering bug. */}
        {MONEY_VISIBLE && (
          <div className={styles['totals']}>
            {order.subtotal_cents !== null && order.subtotal_cents !== order.total_cents && (
              <p className={styles['subtotal']}>
                {t('orderDetail.subtotal')}: {formatMoney({ amount: order.subtotal_cents, currency: order.currency })}
              </p>
            )}
            <p className={styles['total']}>
              {t('orderDetail.total')}: {formatMoney({ amount: order.total_cents, currency: order.currency })}
            </p>
          </div>
        )}
      </header>

      <ul className={styles['items']}>
        {tree.map(({ item, children }) => (
          <ServiceOrderItemRow key={item.item_id} item={item} childItems={children} currency={order.currency} />
        ))}
      </ul>
    </div>
  );
}
